/**
 * The trainer transcript's device copy (`TranscriptCache` in
 * `@repo/client/chat`), kept in the app's persisted query cache under
 * `["chat-transcript", conversationId]`.
 *
 * In the query cache rather than in `preferences` so that it lands in IndexedDB
 * with every other screen's last-known data and is wiped at sign-out with them
 * (`clearCaches` in `data/provider.tsx`: `queryClient.clear()` and the
 * persister's `removeClient()`) — no second store to forget. It is written
 * with `setQueryData` and has no observer and no `queryFn`; the persister
 * keeps any query that has data (`shouldPersistQuery`, `persist.ts`), and
 * `gcTime` is the persister's `maxAge` (`query-client.ts`), so the entry
 * survives seven days unobserved.
 *
 * Imported by the shell's foreground prefetch (`transcript-prefetch.tsx`), so
 * it stays light: nothing here may import the trainer overlay.
 */
import type { ChatMessage, TranscriptCache } from "@repo/client/chat";
import type { QueryClient } from "@tanstack/react-query";

export function transcriptKey(conversationId: string) {
  return ["chat-transcript", conversationId] as const;
}

/**
 * The conversation the person last opened the trainer on — the one the
 * foreground prefetch refreshes. In the same cache, so it is wiped with it.
 */
const LAST_OPENED_KEY = ["chat-transcript-last-opened"] as const;

/**
 * Drop one conversation's copy — the clear control, a clear from another
 * device, a deleted conversation. Removed rather than set to `[]`, so nothing
 * persists an empty entry for a conversation that no longer exists.
 */
export function forgetCachedTranscript(
  queryClient: QueryClient,
  conversationId: string
): void {
  queryClient.removeQueries({
    exact: true,
    queryKey: transcriptKey(conversationId),
  });
}

/**
 * The port over one client. Build it ONCE per client (`useMemo` on the client,
 * or module scope) — it is an effect dependency of `useTrainerChat`.
 */
export function createTranscriptCache(
  queryClient: QueryClient
): TranscriptCache {
  return {
    forget: (conversationId) =>
      forgetCachedTranscript(queryClient, conversationId),
    read: (conversationId) => {
      const cached = queryClient.getQueryData<readonly ChatMessage[]>(
        transcriptKey(conversationId)
      );
      return cached === undefined || cached.length === 0 ? null : cached;
    },
    write: (conversationId, messages) => {
      if (messages.length === 0) {
        forgetCachedTranscript(queryClient, conversationId);
        return;
      }
      queryClient.setQueryData(transcriptKey(conversationId), messages);
    },
  };
}

/** When this conversation's copy was last written, or `0` with none. */
export function cachedTranscriptAt(
  queryClient: QueryClient,
  conversationId: string
): number {
  return (
    queryClient.getQueryState(transcriptKey(conversationId))?.dataUpdatedAt ?? 0
  );
}

export function rememberOpenedConversation(
  queryClient: QueryClient,
  conversationId: string
): void {
  queryClient.setQueryData(LAST_OPENED_KEY, conversationId);
}

/** A deleted conversation is no longer the one to reopen (`D227`). */
export function forgetOpenedConversation(
  queryClient: QueryClient,
  conversationId: string
): void {
  if (lastOpenedConversation(queryClient) === conversationId) {
    queryClient.removeQueries({ exact: true, queryKey: LAST_OPENED_KEY });
  }
}

export function lastOpenedConversation(
  queryClient: QueryClient
): string | null {
  return queryClient.getQueryData<string>(LAST_OPENED_KEY) ?? null;
}

/**
 * How many trainer panels are mounted — each holds a live socket that keeps
 * its transcript current itself, so the foreground prefetch stands down.
 * Module state, because the panel is in a lazy chunk and the prefetch is in
 * the shell's.
 */
let openTrainers = 0;

/** The trainer mounted. Returns its release, for an effect's cleanup. */
export function trainerOpened(): () => void {
  openTrainers += 1;
  let released = false;
  return () => {
    if (!released) {
      released = true;
      openTrainers -= 1;
    }
  };
}

export function isTrainerOpen(): boolean {
  return openTrainers > 0;
}
